import React from 'react';
import { Row, Col, Modal, Form } from 'react-bootstrap';
import Select from 'react-select';
import ExpandingTextArea from 'react-expanding-textarea';
import moment from 'moment';

// Services & Helpers
import GlobalStateService from 'services/GlobalStateService';
import InstructorService from 'services/InstructorService';
import LanguageService from 'services/LanguageService';
import CourseService from 'services/CourseService';
import CustomerService from 'services/CustomerService';
import JobRequestService from 'services/JobRequestService';
import UserService from 'services/UserService';
import LocationService from 'services/LocationService';
import BootboxHelper from 'helpers/BootboxHelper';

// Components
import Loader from 'components/common/Loader';
import DatePicker from 'components/common/DatePicker';
import InstructorListModal from 'components/admin/InstructorListModal';

//-------------------------------------------------------------------------------------------------------------------

class EditJobRequestModal extends React.Component {

    constructor(props) {
        super(props);

        this.instructorListModalRef = React.createRef();

        this.state = {
            isOpen: false,
            timeZones: []
        };
    }

    show(opts) {
        return new Promise((resolve, reject) => {
            this.opts = opts;
            this.jobRequestID = opts.jobRequestID;
            this.jobRequest = opts.jobRequest;
            this.resolve = resolve;
            this.loginInfo = GlobalStateService.getValue('loginInfo');

            this.setState({
                isOpen: true,
                isLoading: true
            });
            this.load();
        });
    }

    close() {
        this.setState({
            isOpen: false
        });
    }

    async load() {
        let jobRequestReq = null;
        if (this.jobRequestID) {
            jobRequestReq = JobRequestService.get(this.jobRequestID);
        }
        const languagesReq = LanguageService.list();
        const coursesReq = CourseService.list();
        const instructorsReq = InstructorService.list();
        //const vendorUsersReq = UserService.list({
        //    role: 'Vendor',
        //    canMakeEnquiries: true
        //});
        const customersReq = CustomerService.list();

        const languages = await languagesReq;
        let languageID;
        const english = languages.find(l => l.name == 'English');
        if (english) {
            languageID = english.id;
        }

        // Get job request from server, or a new one
        let jobRequest;
        if (this.jobRequestID) {
            jobRequest = await jobRequestReq;
        } else {
            jobRequest = {
                languageID,
                type: 'JobRequest',
                numDays: 1,
                timeZone: 'Europe/London',
                instructors: [],
                dates: [],
                dateRuns: [],
                ...this.opts
            };
        }

        this.setState({
            jobRequest,
            languages,
            courses: await coursesReq,
            instructors: await instructorsReq,
            //vendorUsers: await vendorUsersReq,
            customers: await customersReq,
            isLoading: false
        }, () => {
            if (!this.state.jobRequest.dateNotified) {
                this.updateMatchingInstructors();
            }
            this.loadTimeZones();
        });
    }

    async loadTimeZones() {
        const timeZones = await LocationService.listTimeZones(this.state.jobRequest.date);
        this.setState({ timeZones });
    }

    updateFields(values) {
        const jobRequest = { ...this.state.jobRequest };
        let updateMatchingInstructors = false, updateTimeZones = false;
        for (var field in values) {
            let value = values[field];
            jobRequest[field] = value;
            switch (field) {
                case 'languageID':
                case 'includeNonMatchingInstructors':
                case 'includeRampingInstructors':
                case 'minAvailability':
                case 'timeZone':
                case 'numDays':
                case 'excludeNoILT':
                    updateMatchingInstructors = true;
                    break;
                case 'dateRuns':
                case 'startDate':
                    updateMatchingInstructors = true;
                    updateTimeZones = true;
                    break;
                case 'courseID':
                    if (!jobRequest.isSplit) {
                        // Get default number of days
                        const course = this.state.courses.find(c => c.id == value);
                        if (course) {
                            jobRequest.numDays = course.defaultNumDays || 1;
                            if (jobRequest.numDays && jobRequest.isWithJAM) {
                                jobRequest.numDays++;
                            }
                        }
                        updateMatchingInstructors = true;
                    }
                    break;
                case 'isWithJAM':
                    if (!jobRequest.isSplit) {
                        // Add a day for JAM
                        jobRequest.numDays += (value ? 1 : -1);
                    }
                    updateMatchingInstructors = true;
                    break;
                case 'isSplit':
                    if (value && jobRequest.dateRuns.length <= 1 && jobRequest.startDate) {
                        jobRequest.dateRuns[0] = {
                            start: jobRequest.startDate
                        };
                        if (jobRequest.numDays > 0) {
                            jobRequest.dateRuns[0].end = moment(jobRequest.startDate).add(jobRequest.numDays - 1, 'days').toDate();
                        }
                    }
                    updateMatchingInstructors = true;
                    break;
            }
        }
        this.setState({
            jobRequest
        }, () => {
            if (updateMatchingInstructors) {
                this.updateMatchingInstructors();
            }
            if (updateTimeZones) {
                this.loadTimeZones();
            }
        });
    }
    
    async updateMatchingInstructors() {
        if (this.state.isLoadingMatching) {
            clearTimeout(this.tryAgainTimeout);
            this.tryAgainTimeout = setTimeout(() => {
                this.updateMatchingInstructors();
            }, 250);
            return;
        }
        this.setState({ isLoadingMatching: true });
        const jobRequest = { ... this.state.jobRequest };
        const { includeRampingInstructors } = jobRequest;
        delete jobRequest.instructors;
        delete jobRequest.rampingInstructors;
        delete jobRequest.instructions;
        delete jobRequest.includeRampingInstructors;

        // Load matching instructors
        const result = await JobRequestService.listMatchingInstructors(jobRequest);

        // Also load ramping instructors if enabled
        let rampingInstructors = null;
        if (includeRampingInstructors) {
            jobRequest.includeRampingInstructors = true;
            const rampingResult = await JobRequestService.listMatchingInstructors(jobRequest);
            rampingInstructors = rampingResult.instructors;
        }

        // Update
        this.updateFields({
            instructors: result.instructors,
            rampingInstructors,
            dates: result.dates
        });
        this.setState({ isLoadingMatching: false });
    }

    async saveAndClose() {
        this.setState({
            isLoading: true
        });
        try {
            await JobRequestService.save(this.state.jobRequest);
            this.close();
            this.resolve();
        } catch (error) {
            BootboxHelper.alert(error);
            this.setState({
                isLoading: false
            });
        }
    }

    async confirmDelete() {
        const confirm = await BootboxHelper.confirm(`Are you sure you want to delete this job request?`);
        if (confirm) {
            this.delete();
        }
    }

    async delete() {
        this.setState({
            isLoading: true
        });
        await JobRequestService.delete(this.state.jobRequest.id);
        this.close();
        this.resolve();
    }

    async confirmNotify() {
        const {
            jobRequest
        } = this.state;
        const confirm = await BootboxHelper.confirm('Send notification to ' + jobRequest.instructors.length + ' instructor' + (jobRequest.instructors.length == 1 ? '' : 's') + '? (Note: you cannot edit a job request after it has been sent)');
        if (confirm) {
            this.notify();
        }
    }

    async notify() {
        this.setState({ isLoading: true });
        try {
            await JobRequestService.save(this.state.jobRequest);
            await JobRequestService.notify(this.state.jobRequest.id);
            this.close();
            this.resolve();
        } catch (error) {
            BootboxHelper.alert(error);
        }
        this.setState({ isLoading: false });
    }

    viewMatchingInstructors(instructors, isRamping) {
        const { jobRequest } = this.state;
        this.instructorListModalRef.current.show({
            instructors: instructors,
            isRamping,
            dates: jobRequest.dates,
            showNumDeliveries: !!jobRequest.courseID,
            showDailySellRate: jobRequest.showDailySellRate,
            type: jobRequest.type
        });
    }

    updateDateRunFields(index, values) {
        const dateRuns = [...this.state.jobRequest.dateRuns];
        const dateRun = { ...dateRuns[index] };
        for (var field in values) {
            let value = values[field];
            dateRun[field] = value;
        }
        if (dateRun.start && !dateRun.end) {
            dateRun.end = dateRun.start;
        }
        if (dateRun.isNew) {
            delete dateRun.isNew;
            dateRuns.push({ isNew: true });
        }
        if (!dateRun.start && !dateRun.end) {
            dateRuns.splice(index, 1);
        } else {
            dateRuns[index] = dateRun;
        }
        this.updateFields({ dateRuns });
    }

    removeDateRun(index) {
        const dateRuns = [...this.state.jobRequest.dateRuns];
        dateRuns.splice(index, 1);
        this.updateFields({ dateRuns });
    }

    render() {
        const {
            isOpen
        } = this.state;

        if (!isOpen) {
            return null;
        }

        return (<>

            <Modal show className="edit-job-request-modal">
                {this.renderInner()}
            </Modal>

            <InstructorListModal
                ref={this.instructorListModalRef}
                load={() => this.updateMatchingInstructors()}
            />

        </>);
    }

    renderInner() {
        const {
            isLoading,
            jobRequest,
            languages,
            timeZones,
            isLoadingMatching
        } = this.state;

        if (isLoading) {
            return (
                <Modal.Body>
                    <Loader />
                </Modal.Body>
            );
        }
        let courses = [...this.state.courses];
        courses = [ { id: null, name: '(N/A)' } ].concat(courses);

        return (

            <form onSubmit={e => { e.preventDefault(); this.saveAndClose() }}>
                <Modal.Body>

                    {jobRequest.id && !jobRequest.dateNotified && jobRequest.type == 'JobRequest' && <>
                        <div className="alert alert-info">
                            <h2>Ready to send?</h2>
                            <p>Press the button below to email this job request to all matching instructors</p>
                            <button type="button" className="btn btn-primary" onClick={e => this.confirmNotify()}>
                                Send
                            </button>
                        </div>
                    </>}

                    {jobRequest.id && jobRequest.dateNotified && <>
                        <div className="alert alert-info">
                            This job request has been sent, so it cannot be edited
                        </div>
                    </>}

                    {jobRequest.type != 'VendorClassSearch' &&
                        <Form.Group className="mb-3">
                            <Form.Label>Type</Form.Label>
                            <select
                                className="form-control"
                                value={jobRequest.type}
                                disabled={jobRequest.dateNotified}
                                onChange={e => this.updateFields({ type: e.target.value })}
                            >
                                <option value="JobRequest">Job request</option>
                                <option value="SwapSearch">Swap search</option>
                            </select>
                        </Form.Group>
                    }

                    <Row>

                        <Col md="9">

                            <Form.Group className="mb-3">
                                <Form.Label>Course</Form.Label>
                                <Select
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                    value={jobRequest.courseID ? courses.find(c => c.id == jobRequest.courseID) : null}
                                    options={courses}
                                    getOptionValue={c => c.id}
                                    getOptionLabel={c => c.name}
                                    onChange={v => this.updateFields({ courseID: v.id })}
                                    onBlur={e => this.updateMatchingInstructors()}
                                    isDisabled={jobRequest.dateNotified}
                                />
                            </Form.Group>

                        </Col>

                        <Col md="3">

                            <Form.Group>
                                <Form.Label>AWS Jam</Form.Label>
                                <Form.Check
                                    type="checkbox"
                                    id="is-with-jam"
                                    checked={jobRequest.isWithJAM || false}
                                    onChange={e => this.updateFields({ isWithJAM: e.target.checked })}
                                    disabled={jobRequest.dateNotified}
                                    label="With AWS Jam"
                                />
                            </Form.Group>
                            
                        </Col>

                    </Row>

                    {jobRequest.type != 'VendorClassSearch' &&
                        <Form.Group className="mb-3">
                            <Form.Label>Currently Booked on Course</Form.Label>
                            <Select
                                className="react-select-container"
                                classNamePrefix="react-select"
                                value={jobRequest.bookingCourseID ? courses.find(c => c.id == jobRequest.bookingCourseID) : null}
                                options={courses}
                                getOptionValue={c => c.id}
                                getOptionLabel={c => c.name}
                                onChange={v => this.updateFields({ bookingCourseID: v.id })}
                                onBlur={e => this.updateMatchingInstructors()}
                                isDisabled={jobRequest.dateNotified}
                            />
                        </Form.Group>
                    }

                    <Row>

                        <Col md="6">

                            <Form.Group className="mb-3">
                                <Form.Label>Language</Form.Label>
                                <Select
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                    value={jobRequest.languageID ? languages.find(l => l.id == jobRequest.languageID) : null}
                                    options={languages}
                                    getOptionValue={l => l.id}
                                    getOptionLabel={l => l.name}
                                    onChange={v => this.updateFields({ languageID: v.id })}
                                    isDisabled={jobRequest.dateNotified}
                                />
                            </Form.Group>

                        </Col>


                    </Row>

                    <Row>

                        <Col md="6">

                            <Form.Group className="mb-3">
                                <Form.Label>Dates</Form.Label>
                                <Form.Check
                                    type="checkbox"
                                    id="is-split"
                                    checked={jobRequest.isSplit || false}
                                    onChange={e => this.updateFields({ isSplit: e.target.checked })}
                                    label="Split booking dates"
                                />
                                {jobRequest.isSplit &&
                                    this.renderDatesTable()
                                }
                            </Form.Group>

                        </Col>

                        <Col md="3">

                            {!jobRequest.isSplit &&
                                <Form.Group className="mb-3">
                                    <Form.Label>Start Date</Form.Label>
                                    <DatePicker
                                        className="form-control"
                                        selected={jobRequest.startDate ? new Date(jobRequest.startDate) : null}
                                        onChange={value => {
                                            value = (value ? moment(value).format('YYYY-MM-DD') : null);
                                            this.updateFields({
                                                startDate: value
                                            })
                                        }}
                                        readOnly={jobRequest.dateNotified}
                                    />
                                </Form.Group>
                            }

                        </Col>

                        <Col md="3">

                            {!jobRequest.isSplit &&
                                <Form.Group className="mb-3">
                                    <Form.Label>Duration</Form.Label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        value={jobRequest.numDays || ''}
                                        onChange={e => this.updateFields({ numDays: e.target.value })}
                                        readOnly={jobRequest.dateNotified}
                                    />
                                </Form.Group>
                            }

                        </Col>

                    </Row>

                    <Form.Group className="mb-3">
                        <Form.Label>Options</Form.Label>
                        <Form.Check
                            type="checkbox"
                            id="include-non-matching-instructors"
                            checked={jobRequest.includeNonMatchingInstructors || false}
                            onChange={e => this.updateFields({ includeNonMatchingInstructors: e.target.checked })}
                            disabled={jobRequest.dateNotified}
                            label="Include instructors who don't fit the criteria (course, language)"
                        />
                        {!!jobRequest.courseID &&
                            <Form.Check
                                type="checkbox"
                                checked={jobRequest.includeRampingInstructors || false}
                                onChange={e => this.updateFields({ includeRampingInstructors: e.target.checked })}
                                disabled={jobRequest.dateNotified}
                                label="Include instructors ramping on this course"
                            />
                        }
                        <Form.Check
                            type="checkbox"
                            id="exclude-no-ilt"
                            checked={jobRequest.excludeNoILT || false}
                            onChange={e => this.updateFields({ excludeNoILT: e.target.checked })}
                            disabled={jobRequest.dateNotified}
                            label="Exclude instructors who aren't happy to deliver ILT"
                        />
                        {jobRequest.type != 'VendorClassSearch' &&
                            <Form.Check
                                type="checkbox"
                                id="show-margin"
                                checked={!jobRequest.showDailySellRate}
                                onChange={e => this.updateFields({ showDailySellRate: !e.target.checked })}
                                disabled={jobRequest.dateNotified}
                                label="Show margin"
                            />
                        }
                    </Form.Group>

                    <div className="row">

                        <div className="col-6">

                            <Form.Group className="mb-3">
                                <Form.Label>Minimum availability (%)</Form.Label>
                                <input
                                    className="form-control"
                                    key={jobRequest.minAvailability || ''}
                                    type="number"
                                    defaultValue={(Number(jobRequest.minAvailability || '') || '') * 100}
                                    onBlur={e => this.updateFields({ minAvailability: e.target.value / 100 })}
                                    readOnly={jobRequest.dateNotified}
                                />
                            </Form.Group>

                        </div>

                    </div>

                    <Form.Group className="mb-3">
                        <Form.Label>Matching instructors:
                            {' '}{isLoadingMatching ? <Loader isInline /> : jobRequest.instructors.length}
                        </Form.Label>
                        <button type="button" className="btn btn-secondary" onClick={e => this.viewMatchingInstructors(jobRequest.instructors, false)} disabled={!!isLoadingMatching} >
                            View list
                        </button>
                    </Form.Group>

                    {jobRequest.includeRampingInstructors && jobRequest.rampingInstructors &&
                        <Form.Group className="mb-3">
                            <Form.Label>Ramping instructors: {jobRequest.rampingInstructors.length}</Form.Label>
                            <button type="button" className="btn btn-secondary float-right" onClick={e => this.viewMatchingInstructors(jobRequest.rampingInstructors, true)}>
                                View list
                            </button>
                        </Form.Group>
                    }

                    <hr />

                    <Row>

                        <Col md="6">
                            
                            <Form.Group className="mb-3">
                                <Form.Label>Time Zone</Form.Label>
                                <Select
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                    value={jobRequest.timeZone ? timeZones.find(tz => tz.id == jobRequest.timeZone) : null}
                                    options={timeZones}
                                    getOptionValue={tz => tz.id}
                                    getOptionLabel={tz => tz.name}
                                    onChange={v => this.updateFields({ timeZone: v.id })}
                                    isDisabled={jobRequest.dateNotified}
                                />
                            </Form.Group>

                        </Col>

                    </Row>

                    {jobRequest.type != 'VendorClassSearch' &&
                        <Form.Group>
                            <Form.Label>Instructions</Form.Label>
                            <ExpandingTextArea
                                className="form-control"
                                value={jobRequest.instructions || ''}
                                onChange={e => this.updateFields({ instructions: e.target.value })}
                                rows="3"
                                disabled={jobRequest.dateNotified}
                            />
                        </Form.Group>
                    }
                </Modal.Body>
                <Modal.Footer>
                    <button type="button" className="btn btn-secondary me-auto" onClick={() => this.close()}>
                        {jobRequest.dateNotified || jobRequest.type == 'VendorClassSearch' ? 'Close' : 'Cancel'}
                    </button>
                    {jobRequest.id &&
                        <button type="button" className="btn btn-danger" onClick={() => this.confirmDelete()}>
                            Delete
                        </button>
                    }
                    {!jobRequest.dateNotified && jobRequest.type != 'VendorClassSearch' &&
                        <button type="submit" className="btn btn-primary ms-auto">
                            Save
                        </button>
                    }
                </Modal.Footer>
            </form>
        );
    }

    renderDatesTable() {
        const {
            jobRequest
        } = this.state;

        const dateRuns = [...jobRequest.dateRuns];
        if (!dateRuns.find(dr => dr.isNew)) {
            dateRuns.push({ isNew: true });
        }

        return (
            <table className="table table-bordered dates-table">
                <thead>
                    <tr>
                        <th>From</th>
                        <th>To</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    {dateRuns.map((dr, index) =>
                        <tr key={index}>
                            <td>
                                <DatePicker
                                    className="form-control"
                                    placeholder="Select..."
                                    selected={dr.start ? new Date(dr.start) : null}
                                    onChange={value => this.updateDateRunFields(index, { start: moment(value).format('YYYY-MM-DD') })}
                                    minDate={index > 0 && jobRequest.dateRuns[index - 1].end ? new Date(jobRequest.dateRuns[index - 1].end) : null}
                                    readOnly={jobRequest.dateNotified}
                                />
                            </td>
                            <td>
                                {dr.start &&
                                    <DatePicker
                                        className="form-control"
                                        selected={dr.end ? new Date(dr.end) : null}
                                        onChange={value => this.updateDateRunFields(index, { end: moment(value).format('YYYY-MM-DD') })}
                                        minDate={dr.start ? new Date(dr.start) : null}
                                        readOnly={jobRequest.dateNotified}
                                    />
                                }
                            </td>
                            <td>
                                {(dr.start || dr.end) &&
                                    <button type="button" className="btn btn-danger" onClick={() => this.removeDateRun(index)}>
                                        <span className="fa fa-times" />
                                    </button>
                                }
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        );
    }
}

export default EditJobRequestModal;